import { useAtomValue } from 'jotai';
import moment from 'moment';
import React, { useState } from 'react';
import { Collapse } from 'reactstrap';

import TrendingHistoryChart from './TrendingHistoryChart';
import { getChartSubtitle } from '../../../helper';
import {
  appliedIntegrationIdsAtom,
  dashboardDataAtom,
  intervalAtom,
  providerFiltersAtom,
  severityFiltersAtom,
  showHistoryChartCountsAtom,
} from '../../state';
import { BPAHistoryEvent } from '../../types';
import { BPA_HISTORY_COLORS, getBPAProviderColor } from '../config';
import { buildBPAFindingsDrillDownUrl } from '../drillDown';

const HistoryCharts = () => {
  const data = useAtomValue(dashboardDataAtom);
  const interval = useAtomValue(intervalAtom);
  const showCounts = useAtomValue(showHistoryChartCountsAtom);
  const severity = useAtomValue(severityFiltersAtom);
  const provider = useAtomValue(providerFiltersAtom);
  const integrationIds = useAtomValue(appliedIntegrationIdsAtom);
  const [expandedEvent, setExpandedEvent] = useState<
    BPAHistoryEvent | undefined
  >();

  if (!data) return null;

  const labels = data.history.buckets.map((bucket) => bucket.label);
  const historySeries = data.history.series;
  const tooltipLabels = historySeries.map((series) => ({
    label: series.label,
    hostsCount: series.integration_count,
  }));
  const colors = historySeries.map((series) =>
    series.provider
      ? getBPAProviderColor(series.provider)
      : BPA_HISTORY_COLORS[1],
  );
  const latestBucket = data.history.buckets[data.history.buckets.length - 1];
  const currentWeekStartAt =
    data.history.current_week_start_at || latestBucket?.start_at;
  const currentIntervalStartAt = latestBucket?.start_at;
  const currentActiveCount =
    data.current.by_effective_status.find((item) => item.label === 'Active')
      ?.count || 0;
  const selectedProvider = provider.length === 1 ? provider : [];
  const commonScope = {
    severity,
    provider: selectedProvider,
    integrationIds,
  };

  const providerForSource = (sourceType: string) =>
    historySeries.find((series) => series.label === sourceType)?.provider ||
    undefined;

  const getOpenUrl = (sourceType: string) =>
    buildBPAFindingsDrillDownUrl({
      ...commonScope,
      providerOverride: providerForSource(sourceType),
      effectiveStatus: 'Active',
    });

  const getEventUrl = (event: BPAHistoryEvent, sourceType: string) => {
    return currentWeekStartAt
      ? buildBPAFindingsDrillDownUrl({
          ...commonScope,
          providerOverride: providerForSource(sourceType),
          historyType: event,
          historyStartAt: currentWeekStartAt,
        })
      : '';
  };

  const getRollingUrl = (event: BPAHistoryEvent) => {
    if (!currentIntervalStartAt) return '';
    return buildBPAFindingsDrillDownUrl({
      ...commonScope,
      historyType: event,
      historyStartAt: currentIntervalStartAt,
    });
  };

  const getEventTitle = (title: string) =>
    currentIntervalStartAt
      ? `${title} (Since ${moment(currentIntervalStartAt).format('MMM D')})`
      : title;

  const eventProperties = (event: BPAHistoryEvent, title: string) => ({
    title: getEventTitle(title),
    subtitle: getChartSubtitle(title, interval),
    labels,
    tooltipLabels,
    datasets: historySeries.map((series) => series[event]),
    colors,
    showCounts,
    lastIntervalCount: data.history.rolling_counts[event],
    lastIntervalDrillDownUrl: getRollingUrl(event),
    getDrillDownUrl: (sourceType: string) => getEventUrl(event, sourceType),
    newDataGenerationIndex: 0,
    selectedInterval: interval,
    drillDownRequiresWeeklyInterval: true,
    onExpandClick: () =>
      setExpandedEvent((current) => (current === event ? undefined : event)),
  });

  const events: Array<{ key: BPAHistoryEvent; title: string }> = [
    { key: 'new', title: 'New' },
    { key: 'fixed', title: 'Fixed' },
    { key: 'ignored', title: 'Ignored' },
  ];

  return (
    <div className="row">
      <div className="col-12">
        <TrendingHistoryChart
          title="All"
          labels={labels}
          tooltipLabels={tooltipLabels}
          datasets={historySeries.map((series) => series.open)}
          colors={colors}
          showCounts={showCounts}
          getDrillDownUrl={getOpenUrl}
          newDataGenerationIndex={0}
          liveActiveCount={currentActiveCount}
          lastSyncedDate={
            data.scope.history_last_synced_at
              ? moment(data.scope.history_last_synced_at).format('lll')
              : undefined
          }
        />
      </div>

      <Collapse isOpen={Boolean(expandedEvent)} className="col-12">
        {expandedEvent && (
          <TrendingHistoryChart
            key={`expanded-${expandedEvent}`}
            {...eventProperties(
              expandedEvent,
              events.find((event) => event.key === expandedEvent)?.title || '',
            )}
            isActive
          />
        )}
      </Collapse>

      {events.map((event) => (
        <div className="col-md-4" key={event.key}>
          <TrendingHistoryChart
            {...eventProperties(event.key, event.title)}
            isActive={expandedEvent === event.key}
          />
        </div>
      ))}
    </div>
  );
};

export default HistoryCharts;
